import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Compass } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { ActionCard } from "@/components/ui/action-card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ScenarioCriticalityBadge } from "@/components/experience/scenario-criticality-badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function formatRelative(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

/**
 * Lists bound scenarios (outline + persona pairs) directly — each
 * already has a persona cast, so jump directly to the scenario page.
 */
export function QuickStartBanner({
  projectId,
  variant = "secondary",
  asButton = false,
  className,
}: {
  projectId: string;
  variant?: "primary" | "secondary";
  asButton?: boolean;
  className?: string;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data: scenarios } = trpc.scenario.list.useQuery({ projectId });

  const sorted = useMemo(() => {
    if (!scenarios) return [];
    return [...(scenarios as Array<Record<string, unknown>>)].sort((a, b) => {
      const aTime = (a as { lastWalkthroughAt?: string | null })
        .lastWalkthroughAt
        ? new Date(
            (a as { lastWalkthroughAt: string }).lastWalkthroughAt,
          ).getTime()
        : 0;
      const bTime = (b as { lastWalkthroughAt?: string | null })
        .lastWalkthroughAt
        ? new Date(
            (b as { lastWalkthroughAt: string }).lastWalkthroughAt,
          ).getTime()
        : 0;
      return bTime - aTime;
    });
  }, [scenarios]);

  if (sorted.length === 0) return null;

  return (
    <>
      {asButton ? (
        <Button
          variant="outline"
          size="sm"
          className={cn("w-full justify-start", className)}
          onClick={() => setOpen(true)}
        >
          <Compass className="mr-2 h-4 w-4" />
          Jump to scenario
        </Button>
      ) : (
        <ActionCard
          variant={variant}
          icon={Compass}
          label="Jump to scenario"
          onClick={() => setOpen(true)}
          className={className}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Jump to scenario</DialogTitle>
            <DialogDescription>
              Select a scenario to view its persona journey and past runs.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 max-h-[60vh] space-y-1 overflow-y-auto">
            {sorted.map((raw) => {
              const s = raw as {
                id: string;
                outlineId?: string | null;
                personaId?: string | null;
                outlineName?: string | null;
                name?: string | null;
                personaName?: string | null;
                walkthroughCount?: number;
                lastWalkthroughAt?: string | null;
                outlineTaskCriticality?: string | null;
                taskCriticality?: string | null;
              };
              const name = s.outlineName ?? s.name ?? "Scenario";
              const personaName = s.personaName ?? null;
              const walkthroughCount = s.walkthroughCount ?? 0;
              const criticality =
                s.outlineTaskCriticality ?? s.taskCriticality ?? null;
              // Build the path to the unified scenario page. Hash anchor
              // navigates to the persona panel when available; otherwise
              // we land on the scenario header. We do NOT auto-trigger a
              // run here — the persona panel exposes its own Run CTA, and
              // the previous `?run=true` autorun shortcut lived on the
              // deleted per-persona page. If `outlineId` isn't on the row,
              // we have nothing to link to (the row is malformed).
              const scenarioPath = s.outlineId
                ? s.personaId
                  ? `/projects/${projectId}/scenarios/${s.outlineId}#persona-${s.personaId}`
                  : `/projects/${projectId}/scenarios/${s.outlineId}`
                : null;
              if (!scenarioPath) return null;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate(scenarioPath);
                  }}
                  className="group/item flex w-full items-start gap-3 rounded-xl px-4 py-3 text-left transition-colors hover:bg-muted/60"
                >
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-base font-semibold text-foreground">
                      {name}
                    </p>
                    {personaName && (
                      <p className="mt-0.5 truncate text-sm text-muted-foreground">
                        As experienced by {personaName}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2 pt-0.5">
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {walkthroughCount > 0
                        ? `${walkthroughCount} run${walkthroughCount !== 1 ? "s" : ""} · ${formatRelative(s.lastWalkthroughAt)}`
                        : "No runs yet"}
                    </span>
                    {criticality === "critical_path" && (
                      <ScenarioCriticalityBadge criticality={criticality} />
                    )}
                    <ChevronRight
                      className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover/item:translate-x-0.5 group-hover/item:text-foreground"
                      aria-hidden="true"
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
